import { Box, Button, FormHelperText, Link, Typography } from "@mui/material";
import { Form, Formik, Field, FieldProps } from "formik";
import * as React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import * as Yup from "yup";

import { FormikCheckField } from "components/formik/FormikCheckField";
import { FormikTextField } from "components/formik/FormikTextField";

export interface RegisterFormState {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  policy: boolean;
  marketing: boolean;
  submit: string | null;
  captcha: string;
}

const initialValuesOuter: RegisterFormState = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  policy: false,
  marketing: false,
  submit: null,
  captcha: "",
};

const validationSchemaOuter = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Must be a valid email")
    .max(255)
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Must be at least 8 characters")
    .max(255)
    .required("Required")
    .matches(/(?=.*[A-Z])/gu, "Must have at least one uppercase")
    .matches(/(?=.*[a-z])/gu, "Must have at least one lowercase")
    .matches(/(?=.*\d)/gu, "Must have at least one digit")
    .matches(/(?=.*\W)/gu, "Must have at least one symbol"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Password is required"),
  policy: Yup.boolean().oneOf([true], "This field must be checked"),
  captcha: Yup.string().required("Required"),
});

interface RegisterFormProps {
  onSubmit: (values: RegisterFormState) => Promise<void>;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSubmit }) => (
  <Formik
    initialValues={initialValuesOuter}
    validationSchema={validationSchemaOuter}
    onSubmit={onSubmit}
  >
    {({ errors, isSubmitting, setFieldValue }): JSX.Element => (
      <Form>
        <FormikTextField
          name="firstName"
          fullWidth
          label="First Name"
          type="text"
        />
        <FormikTextField
          name="lastName"
          fullWidth
          label="Last Name"
          type="text"
        />
        <FormikTextField
          name="email"
          fullWidth
          label="Email Address"
          type="email"
        />
        <FormikTextField
          name="password"
          fullWidth
          label="Password"
          type="password"
        />
        <FormikTextField
          name="confirmPassword"
          fullWidth
          label="Confirm Password"
          type="password"
        />
        <FormikCheckField
          name="policy"
          label={
            <>
              I agree to the{" "}
              <Link color="primary" component="a" href="#">
                Terms and Conditions
              </Link>
            </>
          }
        />
        <FormikCheckField
          name="marketing"
          label={
            <Typography>
              I would like to receive marketing and promotional materials from{" "}
              <b>MUSIC CARE</b>.
            </Typography>
          }
        />
        <Field name="captcha">
          {({ meta }: FieldProps<string>) => (
            <>
              <ReCAPTCHA
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ""}
                onChange={(e: string | null) => {
                  setFieldValue("captcha", e);
                }}
              />
              <Typography variant="body2" color="error">
                {meta.touched && meta.error
                  ? "This field must be checked"
                  : null}
              </Typography>
            </>
          )}
        </Field>
        {errors.submit && (
          <Box sx={{ mt: 3 }}>
            <FormHelperText error>{errors.submit}</FormHelperText>
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <Button
            color="secondary"
            disabled={isSubmitting}
            fullWidth
            size="large"
            type="submit"
            variant="contained"
          >
            Sign up
          </Button>
        </Box>
      </Form>
    )}
  </Formik>
);
